import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraAuth } from '../contexts/AraAuthContext';
import { useAraActiveSession } from '../contexts/AraActiveSessionContext';
import { useAraTheme } from '../contexts/AraThemeContext';
import { getAllAraEncrypted, deleteAraData, ARA_STORES } from '../utils/ara-storage';
import { ArrowLeft, ChatCircleDots, Trash, X, ArrowRight } from 'phosphor-react';
import { toast } from 'sonner';

const stripMarkdown = (t) => (t || '')
  .replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1')
  .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g, '$1$2')
  .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s.,!?)]|$)/g, '$1$2')
  .replace(/`([^`\n]+)`/g, '$1')
  .replace(/^#{1,6}\s+/gm, '').replace(/^[\s]*[-*+]\s+/gm, '').replace(/^>\s+/gm, '');

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

const AraConversations = () => {
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { encryptionKey } = useAraAuth();
  const { restoreSession } = useAraActiveSession();
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  const load = async () => {
    if (!encryptionKey) return;
    setIsLoading(true);
    try {
      const data = await getAllAraEncrypted(ARA_STORES.SESSIONS, encryptionKey).catch(() => []);
      data.sort((a, b) => new Date(b.updatedAt || b.startedAt || 0) - new Date(a.updatedAt || a.startedAt || 0));
      setList(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [encryptionKey]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const remove = async (id) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await deleteAraData(ARA_STORES.SESSIONS, id);
      setList((prev) => prev.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Conversation deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  const continueConversation = (session) => {
    restoreSession(session);
    navigate('/ara/chat');
  };

  return (
    <div className="ara-root min-h-screen" data-ara-theme={theme} data-testid="ara-conversations">
      <header className="ara-header sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/ara/chat')}
            className="inline-flex items-center gap-2 ara-icon-btn p-2 rounded-lg text-sm"
            data-testid="ara-conv-back-btn"
            aria-label="Back to Ara"
          >
            <ArrowLeft size={18} /> <span className="hidden sm:inline">Back to Ara</span>
          </button>
          <img src={logo} alt="" className="w-8 h-8 rounded-full ml-1" />
          <h1 className="font-serif font-semibold ara-text" data-testid="ara-conversations-title">Conversations</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-16 ara-text-muted">Opening your conversations…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16" data-testid="ara-conversations-empty">
            <ChatCircleDots size={32} weight="duotone" className="ara-accent mx-auto mb-3" />
            <p className="ara-text font-medium mb-1">No conversations yet</p>
            <p className="text-sm ara-text-muted max-w-sm mx-auto">
              Start a chat with Ara and it will appear here automatically. You can
              return to any past conversation anytime.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((r) => (
              <div
                key={r.id}
                className="ara-card rounded-2xl p-5 cursor-pointer hover:ara-card-hover transition-all"
                onClick={() => setSelected(r)}
                data-testid={`ara-conversation-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium ara-text mb-1 truncate">{r.title || 'Untitled conversation'}</h3>
                    <p className="text-xs ara-text-muted">
                      {fmtDate(r.updatedAt || r.startedAt)} • {fmtTime(r.updatedAt || r.startedAt)}
                      {typeof r.messageCount === 'number' && (<span> • {r.messageCount} messages</span>)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                    className="ara-icon-btn p-2 rounded-lg"
                    data-testid={`ara-conversation-delete-${r.id}`}
                    aria-label="Delete conversation"
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {selected && (
        <div
          className="fixed inset-0 z-20 ara-root flex flex-col"
          data-ara-theme={theme}
          data-testid="ara-conversation-detail"
          role="dialog"
          aria-modal="true"
        >
          <header className="ara-header sticky top-0 z-10">
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <button
                onClick={() => setSelected(null)}
                className="inline-flex items-center gap-2 ara-icon-btn p-2 rounded-lg text-sm"
                data-testid="ara-conv-detail-back-btn"
              >
                <ArrowLeft size={18} /> <span>Back</span>
              </button>
              <button
                onClick={() => continueConversation(selected)}
                className="ara-cta-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
                data-testid="ara-conv-continue-btn"
              >
                Continue conversation <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6 pb-12">
              <h2 className="text-2xl font-serif font-semibold ara-text mb-2">
                {selected.title || 'Untitled conversation'}
              </h2>
              <p className="text-xs ara-text-muted mb-6">
                {fmtDate(selected.updatedAt || selected.startedAt)} • {fmtTime(selected.updatedAt || selected.startedAt)}
              </p>
              {Array.isArray(selected.messages) && selected.messages.length > 0 ? (
                <div className="space-y-4" data-testid="ara-conv-detail-messages">
                  {selected.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-5 py-3 ${m.role === 'user' ? 'ara-msg-user' : 'ara-msg-assistant'}`}>
                        <div className="text-[11px] uppercase tracking-wide ara-text-muted mb-1 font-medium">
                          {m.role === 'user' ? 'You' : 'Ara'}
                        </div>
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {m.role === 'assistant' ? stripMarkdown(m.content) : m.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ara-card rounded-2xl p-6 text-center">
                  <p className="ara-text font-medium mb-1">This conversation's messages aren't available to display.</p>
                  <p className="text-sm ara-text-muted">Your record remains saved and untouched.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AraConversations;
